/** Development-only phase profile for the FIT decode -> extraction -> normalization pipeline. */
export type RideParserPhaseCounts = {
  /** Real FIT message count: sum of the decoded top-level message-group lengths. */
  fitMessageCount: number | null;
  recordMessageCount: number | null;
  sessionMessageCount: number | null;
  lapMessageCount: number | null;
  eventMessageCount: number | null;
  deviceInfoMessageCount: number | null;
  developerFieldMessageCount: number | null;
  ignoredMessageGroupCount: number | null;
  /** Nodes visited by the structural traversal that finds record messages. */
  traversedNodeCount: number | null;
  recordCandidatesMapped: number | null;
  recordCandidatesSkippedAsDuplicate: number | null;
  recordCandidatesExcludedByPath: number | null;
  normalizedSamplesProduced: number | null;
};

export type RideParserPhaseProfile = {
  decoderSetupMs: number | null;
  binaryDecodeMs: number | null;
  summarySelectionMs: number | null;
  summaryFieldExtractionMs: number | null;
  recordExtractionMs: number | null;
  recordNormalizationMs: number | null;
  summaryReconciliationMs: number | null;
  analyticsMs: number | null;
  resultConstructionMs: number | null;
  totalMs: number | null;
  counts: RideParserPhaseCounts;
};

export function createRideParserPhaseProfile(): RideParserPhaseProfile {
  return {
    decoderSetupMs: null,
    binaryDecodeMs: null,
    summarySelectionMs: null,
    summaryFieldExtractionMs: null,
    recordExtractionMs: null,
    recordNormalizationMs: null,
    summaryReconciliationMs: null,
    analyticsMs: null,
    resultConstructionMs: null,
    totalMs: null,
    counts: {
      fitMessageCount: null,
      recordMessageCount: null,
      sessionMessageCount: null,
      lapMessageCount: null,
      eventMessageCount: null,
      deviceInfoMessageCount: null,
      developerFieldMessageCount: null,
      ignoredMessageGroupCount: null,
      traversedNodeCount: null,
      recordCandidatesMapped: null,
      recordCandidatesSkippedAsDuplicate: null,
      recordCandidatesExcludedByPath: null,
      normalizedSamplesProduced: null
    }
  };
}

const RECORD_GROUP_KEYS = ["records", "record"];
const SESSION_GROUP_KEYS = ["sessions", "session"];
const LAP_GROUP_KEYS = ["laps", "lap"];
const EVENT_GROUP_KEYS = ["events", "event"];
const DEVICE_GROUP_KEYS = ["device_infos", "deviceInfos", "device_info", "deviceInfo", "devices"];
const DEVELOPER_GROUP_KEYS = ["developer_data_ids", "developerDataIds", "field_descriptions", "fieldDescriptions"];

function groupLength(root: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value.length;
    }
  }
  return null;
}

/**
 * Counts decoded FIT messages by summing the lengths of the top-level message groups.
 * This replaces a recursive whole-tree node counter that reported ~2.2M for a 14k-record file.
 */
export function summarizeDecodedFitMessageCounts(data: unknown): RideParserPhaseCounts {
  const empty: RideParserPhaseCounts = createRideParserPhaseProfile().counts;
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return empty;
  }

  const root = data as Record<string, unknown>;
  const recordMessageCount = groupLength(root, RECORD_GROUP_KEYS);
  const sessionMessageCount = groupLength(root, SESSION_GROUP_KEYS);
  const lapMessageCount = groupLength(root, LAP_GROUP_KEYS);
  const eventMessageCount = groupLength(root, EVENT_GROUP_KEYS);
  const deviceInfoMessageCount = groupLength(root, DEVICE_GROUP_KEYS);
  const developerFieldMessageCount = groupLength(root, DEVELOPER_GROUP_KEYS);

  const knownGroupKeys = new Set([
    ...RECORD_GROUP_KEYS,
    ...SESSION_GROUP_KEYS,
    ...LAP_GROUP_KEYS,
    ...EVENT_GROUP_KEYS,
    ...DEVICE_GROUP_KEYS,
    ...DEVELOPER_GROUP_KEYS
  ]);

  let ignoredMessageGroupCount = 0;
  let otherMessages = 0;
  for (const [key, value] of Object.entries(root)) {
    if (knownGroupKeys.has(key) || !Array.isArray(value)) {
      continue;
    }
    ignoredMessageGroupCount += 1;
    otherMessages += value.length;
  }

  const known = [recordMessageCount, sessionMessageCount, lapMessageCount, eventMessageCount, deviceInfoMessageCount, developerFieldMessageCount]
    .filter((value): value is number => typeof value === "number");

  return {
    ...empty,
    fitMessageCount: known.reduce((total, value) => total + value, 0) + otherMessages,
    recordMessageCount,
    sessionMessageCount,
    lapMessageCount,
    eventMessageCount,
    deviceInfoMessageCount,
    developerFieldMessageCount,
    ignoredMessageGroupCount
  };
}
